// Shapes and labels for the document coverage screen. Mirrors
// api/src/handlers/statements/getEvidenceCoverage.ts.

export type EvidenceCoverageLine = {
  line_code: string
  figures: number
  with_document: number
  rule_backed: number
  missing: number
  pct_accounted_for: number
  zar_missing: number
  first_month_missing: string | null
  last_month_missing: string | null
  months_with_gaps: number
  months_clean: number
}

export type EvidenceCoverageMonth = {
  month: string
  line_code: string
  figures: number
  with_document: number
  rule_backed: number
  missing: number
  zar_missing: number
}

export type EvidenceCoverage = {
  lines: EvidenceCoverageLine[]
  months: EvidenceCoverageMonth[]
}

export const EMPTY_COVERAGE: EvidenceCoverage = { lines: [], months: [] }

// The statement's own wording for each line, so this screen and the statement
// name the same thing. A line_code that is not here is humanised rather than
// hidden: a new charge appearing with an ugly label is a smaller problem than a
// new charge that never shows up at all.
const LINE_LABELS: Record<string, string> = {
  insurance: 'Insurance',
  telemetric_system: 'Telematics',
  registration_costs: 'Registration',
  maintenance_costs: 'Maintenance',
  carwashes: 'Carwashes',
  petrol: 'Petrol',
  earnings: 'Earnings',
  loss_of_use: 'Loss of use',
}

export function lineLabel(code: string): string {
  const known = LINE_LABELS[code]
  if (known) return known
  const words = code.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// Why a line can be fully accounted for with zero documents. Carwashes are the
// only one today, and stating the reason on the row is what stops somebody
// "fixing" a green line by going to look for invoices that do not exist.
export function backingNote(row: EvidenceCoverageLine): string | null {
  if (row.rule_backed > 0 && row.with_document === 0) {
    return 'Charged by rule, not by invoice. There is no per-vehicle document to hold up.'
  }
  if (row.rule_backed > 0) return 'Part document, part rule.'
  return null
}

// "2026-07" from the by-month view, shown the way the rest of the app shows a
// period.
export function monthLabel(value: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(value)
  if (!m) return value
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' })
}
